import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';
import type {
  UserCreditWalletResponse,
  UserDetail,
  UsersResponse,
} from '@/types/users';

export type UsersSortBy =
  | 'signup_date'
  | 'email'
  | 'name'
  | 'plan'
  | 'cancel_at_period_end'
  | 'total_chats'
  | 'total_routines'
  | 'total_routine_templates'
  | 'total_journals';

export type UsersSortDir = 'asc' | 'desc';

export interface UsersFilters {
  plans?: string[];
  professions?: string[];
  featureAccess?: string[];
  isActive?: boolean;
  hasPlusOverride?: boolean;
  cancelAtPeriodEnd?: boolean;
  signupDateFrom?: string;
  signupDateTo?: string;
}

export interface UsersQueryOptions {
  page: number;
  limit: number;
  search?: string;
  sortBy?: UsersSortBy;
  sortDir?: UsersSortDir;
  filters?: UsersFilters;
}

export function useUsers({
  page,
  limit,
  search,
  sortBy,
  sortDir,
  filters,
}: UsersQueryOptions) {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['users', { page, limit, search, sortBy, sortDir, filters }],
    queryFn: async (): Promise<UsersResponse> => {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (search) params.append('search', search);
      if (sortBy) params.append('sort_by', sortBy);
      if (sortDir) params.append('sort_dir', sortDir);

      if (filters) {
        filters.plans?.forEach((plan) => {
          if (plan) {
            params.append('plan', plan);
          }
        });

        filters.professions?.forEach((profession) => {
          if (profession) {
            params.append('profession', profession);
          }
        });

        filters.featureAccess?.forEach((feature) => {
          if (feature) {
            params.append('feature_access', feature);
          }
        });

        if (filters.isActive === false) {
          params.append('is_active', 'false');
        }

        if (filters.hasPlusOverride !== undefined) {
          params.append('has_plus_override', String(filters.hasPlusOverride));
        }

        if (filters.cancelAtPeriodEnd !== undefined) {
          params.append(
            'cancel_at_period_end',
            String(filters.cancelAtPeriodEnd)
          );
        }

        if (filters.signupDateFrom) {
          params.append('signup_date_from', filters.signupDateFrom);
        }

        if (filters.signupDateTo) {
          params.append('signup_date_to', filters.signupDateTo);
        }
      }

      const response = await apiFetch(
        `${API_URL}/admin/users?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message || 'Failed to fetch users'
        );
      }

      return await response.json();
    },
    enabled: true,
  });
}

export function useUser(userId: number | null) {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['user', userId],
    queryFn: async (): Promise<UserDetail | null> => {
      if (!userId) return null;

      const token = await getAccessTokenSilently();
      const response = await apiFetch(`${API_URL}/admin/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message || 'Failed to fetch user'
        );
      }

      return await response.json();
    },
    enabled: !!userId,
  });
}

export interface CreditWalletFilters {
  sources?: string[];
  features?: string[];
  since?: string | null;
  minAmount?: number | null;
}

export function useUserCreditWallet(
  userId: number | null,
  page = 1,
  limit = 100,
  filters: CreditWalletFilters = {}
) {
  const { getAccessTokenSilently } = useAuth0();
  const {
    sources = [],
    features = [],
    since = null,
    minAmount = null,
  } = filters;

  return useQuery({
    queryKey: ['user-credit-wallet', userId, page, limit, filters],
    queryFn: async (): Promise<UserCreditWalletResponse | null> => {
      if (!userId) return null;

      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      for (const source of sources) {
        params.append('source', source);
      }
      for (const feature of features) {
        params.append('feature', feature);
      }
      if (since) {
        params.set('since', since);
      }
      if (minAmount != null) {
        params.set('min_abs_delta', minAmount.toString());
      }
      const response = await apiFetch(
        `${API_URL}/admin/users/${userId}/credit-wallet?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message ||
            errorData.detail ||
            'Failed to fetch credit wallet'
        );
      }

      return await response.json();
    },
    enabled: !!userId,
  });
}

export function useUpdateUser() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      plan,
      feature_flags,
    }: {
      userId: number;
      plan?: string;
      feature_flags?: Partial<UserDetail['feature_flags']>;
    }) => {
      const token = await getAccessTokenSilently();
      const body: {
        plan?: string;
        feature_flags?: Partial<UserDetail['feature_flags']>;
      } = {};
      if (plan !== undefined) body.plan = plan;
      if (feature_flags !== undefined) body.feature_flags = feature_flags;

      const response = await apiFetch(`${API_URL}/admin/users/${userId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update user');
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
}

export function useDeleteUser() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: number) => {
      const token = await getAccessTokenSilently();
      const response = await apiFetch(
        `${API_URL}/admin/users/${userId}/hard-delete`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to delete user');
      }

      return await response.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useGenerateCoupon() {
  const { getAccessTokenSilently } = useAuth0();
  return useMutation({
    mutationFn: async ({
      email,
      plan,
      discount_percentage,
      duration_months,
    }: {
      email: string;
      plan: string;
      discount_percentage: number;
      duration_months: number | null;
    }) => {
      const body: Record<string, unknown> = {
        email,
        plan,
        discount_percentage,
      };
      if (duration_months !== null) body.duration_months = duration_months;
      const res = await apiFetch(`${API_URL}/admin/generate-coupon`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await getAccessTokenSilently()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok)
        throw new Error(
          (await res.json().catch(() => ({}))).detail ||
            'Failed to generate coupon'
        );
      return res.json();
    },
  });
}

export function useCancelSubscription() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      const res = await apiFetch(
        `${API_URL}/admin/users/${userId}/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!res.ok)
        throw new Error(
          (await res.json().catch(() => ({}))).detail ||
            'Failed to cancel subscription'
        );
      return res.json();
    },
    onSuccess: () => (
      queryClient.invalidateQueries({ queryKey: ['users'] }),
      queryClient.invalidateQueries({ queryKey: ['user'] })
    ),
  });
}

export function useGrantTrial() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      plan,
      trial_end,
    }: {
      userId: number;
      plan: string;
      trial_end: string;
    }) => {
      const res = await apiFetch(
        `${API_URL}/admin/users/${userId}/grant-trial`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ plan, trial_end }),
        }
      );
      if (!res.ok)
        throw new Error(
          (await res.json().catch(() => ({}))).detail || 'Failed to grant trial'
        );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
}

export function useAddCredits() {
  const { getAccessTokenSilently } = useAuth0();
  return useMutation({
    mutationFn: async ({
      userId,
      amount_cents,
      description,
    }: {
      userId: number;
      amount_cents: number;
      description?: string;
    }) => {
      const res = await apiFetch(
        `${API_URL}/admin/users/${userId}/add-credits`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ amount_cents, description }),
        }
      );
      if (!res.ok)
        throw new Error(
          (await res.json().catch(() => ({}))).detail || 'Failed to add credits'
        );
      return res.json();
    },
  });
}

export function useResetDailyQuota() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      const res = await apiFetch(
        `${API_URL}/admin/users/${userId}/chat-limits/reset`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
          },
        }
      );
      if (!res.ok)
        throw new Error(
          (await res.json().catch(() => ({}))).detail?.user_error_message ||
            'Failed to reset quota'
        );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
}

export function useAddUsageCredits() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      amountUsd,
      reason,
    }: {
      userId: number;
      amountUsd: number;
      reason?: string;
    }) => {
      const res = await apiFetch(
        `${API_URL}/admin/users/${userId}/credits/add-usage`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ amount_usd: amountUsd, reason }),
        }
      );
      if (!res.ok)
        throw new Error(
          (await res.json().catch(() => ({}))).detail ||
            'Failed to add usage credits'
        );
      return res.json();
    },
    onSuccess: (_data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({
        queryKey: ['user-credit-wallet', userId],
      });
    },
  });
}

export function useRemoveUsageCredits() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      amountUsd,
      reason,
    }: {
      userId: number;
      amountUsd: number;
      reason?: string;
    }) => {
      const res = await apiFetch(
        `${API_URL}/admin/users/${userId}/credits/remove-usage`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ amount_usd: amountUsd, reason }),
        }
      );
      if (!res.ok)
        throw new Error(
          (await res.json().catch(() => ({}))).detail ||
            'Failed to remove usage credits'
        );
      return res.json() as Promise<{
        balance: number;
        purchased_balance: number;
      }>;
    },
    onSuccess: (_data, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({
        queryKey: ['user-credit-wallet', userId],
      });
    },
  });
}

export function useResetDailyAllowance() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      const res = await apiFetch(
        `${API_URL}/admin/users/${userId}/daily-allowance/reset`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
          },
        }
      );
      if (!res.ok)
        throw new Error(
          (await res.json().catch(() => ({}))).detail ||
            'Failed to reset daily allowance'
        );
      return res.json();
    },
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({
        queryKey: ['user-credit-wallet', userId],
      });
    },
  });
}

export function useResetMonthlyAllowance() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number) => {
      const res = await apiFetch(
        `${API_URL}/admin/users/${userId}/monthly-allowance/reset`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
          },
        }
      );
      if (!res.ok)
        throw new Error(
          (await res.json().catch(() => ({}))).detail ||
            'Failed to reset monthly allowance'
        );
      return res.json();
    },
    onSuccess: (_data, userId) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({
        queryKey: ['user-credit-wallet', userId],
      });
    },
  });
}

export function useApplyReferral() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      referral_code,
    }: {
      userId: number;
      referral_code: string;
    }) => {
      const res = await apiFetch(
        `${API_URL}/admin/users/${userId}/apply-referral`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${await getAccessTokenSilently()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ referral_code }),
        }
      );
      if (!res.ok)
        throw new Error(
          (await res.json().catch(() => ({}))).detail ||
            'Failed to apply referral'
        );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
}
